import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Tab, Tabs } from '@mui/material';
import { Groups, TrendingUp } from '@mui/icons-material';
import OwnerAnalyticsTab from './owner/OwnerAnalyticsTab';
import TeamTab from './owner/TeamTab';
import { PERIOD_OPTIONS, type PeriodKey } from './owner/ownerData';
import { Page, PageHeader } from '../../components/layout/Page';

const TABS = [
  { id: 'overview', label: 'Business overview', icon: <TrendingUp /> },
  { id: 'team', label: 'Team', icon: <Groups /> },
] as const;
type TabId = (typeof TABS)[number]['id'];

const isPeriod = (v: string | null): v is PeriodKey => PERIOD_OPTIONS.some((p) => p.value === v);

/**
 * The owner's Analytics page. Both tabs read the owner RPCs and speak the same
 * periods; the tab and period live in the URL (?tab=team&period=qtd), so a
 * view can be bookmarked or sent, and switching tabs keeps the period.
 */
const AnalyticsDashboard: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const tab: TabId = params.get('tab') === 'team' ? 'team' : 'overview';
  const rawPeriod = params.get('period');
  const period: PeriodKey = isPeriod(rawPeriod) ? rawPeriod : 'full4';

  const update = useMemo(
    () => (key: 'tab' | 'period', value: string, fallback: string) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === fallback) next.delete(key);
          else next.set(key, value);
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );

  return (
    <Page width="wide">
      <Box>
        <PageHeader title="Analytics" />
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tab} onChange={(_e, v: TabId) => update('tab', v, 'overview')} aria-label="Analytics views" variant="scrollable" allowScrollButtonsMobile>
            {TABS.map((t) => (
              <Tab key={t.id} value={t.id} label={t.label} icon={t.icon} iconPosition="start" id={`analytics-tab-${t.id}`} aria-controls={`analytics-panel-${t.id}`} />
            ))}
          </Tabs>
        </Box>
        <Box role="tabpanel" id={`analytics-panel-${tab}`} aria-labelledby={`analytics-tab-${tab}`}>
          {tab === 'overview' ? (
            <OwnerAnalyticsTab period={period} onPeriodChange={(p) => update('period', p, 'full4')} />
          ) : (
            <TeamTab period={period} onPeriodChange={(p) => update('period', p, 'full4')} />
          )}
        </Box>
      </Box>
    </Page>
  );
};

export default AnalyticsDashboard;
